import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Loader2,
  Play,
  RefreshCw,
  Search,
  Trash2
} from 'lucide-react';
import {
  AdminPhotoCleanupJobStatus,
  AdminPhotoCleanupResponse,
  dryRunPhotoCleanup,
  getPhotoCleanupStatus,
  startPhotoCleanup
} from '../api/photoCleanup';
import PageHeader from '../components/shared/PageHeader';
import { ErrorBanner } from '../components/shared/ErrorBanner';

const POLL_INTERVAL_MS = 45_000;

const fmt = (value: number | null | undefined) => (value ?? 0).toLocaleString('en-US');

const formatDateTime = (value: string | null) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(new Date(value));
};

const formatDuration = (start: string | null, end: string | null) => {
  if (!start) return '-';
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  const totalSeconds = Math.max(0, Math.floor((endMs - startMs) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

const stateTone = (state: string) => {
  if (state === 'RUNNING') return 'warning';
  if (state === 'COMPLETED') return 'success';
  if (state === 'FAILED' || state === 'COMPLETED_WITH_ERRORS') return 'danger';
  return 'outline';
};

const Kpi: React.FC<{ label: string; value: number | string; sub?: string }> = ({ label, value, sub }) => (
  <div className="hf-kpi">
    <div className="hf-kpi-label">{label}</div>
    <div className="hf-kpi-row">
      <div className="hf-kpi-value">{typeof value === 'number' ? fmt(value) : value}</div>
    </div>
    {sub && <div className="hf-kpi-sub">{sub}</div>}
  </div>
);

const OptionToggle: React.FC<{
  checked: boolean;
  disabled?: boolean;
  title: string;
  description: string;
  onChange: (checked: boolean) => void;
}> = ({ checked, disabled, title, description, onChange }) => (
  <label
    className="hf-card"
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      padding: 12,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.65 : 1
    }}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      style={{ marginTop: 2 }}
    />
    <span style={{ minWidth: 0 }}>
      <span style={{ display: 'block', fontWeight: 600, fontSize: 13, color: 'var(--fg-1)' }}>{title}</span>
      <span className="hf-help" style={{ display: 'block', marginTop: 3 }}>{description}</span>
    </span>
  </label>
);

const PhotoCleanupPage: React.FC = () => {
  const [status, setStatus] = useState<AdminPhotoCleanupJobStatus | null>(null);
  const [dryRun, setDryRun] = useState<AdminPhotoCleanupResponse | null>(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [removeInactiveGalleryPhotos, setRemoveInactiveGalleryPhotos] = useState(true);
  const [removeBlacklistedPhotos, setRemoveBlacklistedPhotos] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestOptions = useMemo(() => ({
    removeInactiveGalleryPhotos,
    removeBlacklistedPhotos
  }), [removeInactiveGalleryPhotos, removeBlacklistedPhotos]);

  const loadStatus = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoadingStatus(true);
    try {
      const result = await getPhotoCleanupStatus();
      setStatus(result);
      setLastRefreshedAt(new Date());
    } catch (err) {
      console.error('Failed to load photo cleanup status', err);
      setError('Failed to load photo cleanup status');
    } finally {
      if (showSpinner) setLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus(true);
    const timer = window.setInterval(() => {
      void loadStatus(false);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [loadStatus]);

  const handleDryRun = async () => {
    setPreviewing(true);
    setError(null);
    try {
      const result = await dryRunPhotoCleanup(requestOptions);
      setDryRun(result);
    } catch (err) {
      console.error('Photo cleanup dry run failed', err);
      setError('Photo cleanup dry run failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleStart = async () => {
    if (status?.running || starting) return;
    const ok = window.confirm(
      'Start photo cleanup? This will update DB rows first and then delete selected MinIO objects.'
    );
    if (!ok) return;

    setStarting(true);
    setError(null);
    try {
      const result = await startPhotoCleanup(requestOptions);
      setStatus(result);
      setDryRun(null);
      setLastRefreshedAt(new Date());
    } catch (err) {
      console.error('Failed to start photo cleanup', err);
      setError('Failed to start photo cleanup');
    } finally {
      setStarting(false);
    }
  };

  const processedObjects = (status?.objectsDeleted ?? 0) + (status?.objectsFailed ?? 0);
  const totalObjects = status?.totalObjects ?? 0;
  const progress = totalObjects > 0 ? Math.min(100, Math.round((processedObjects / totalObjects) * 100)) : 0;
  const currentState = status?.state ?? 'IDLE';
  const dryRunObjectsToDelete = dryRun?.objectKeysToDeleteCount ?? dryRun?.objectKeysToDelete?.length ?? 0;

  return (
    <>
      <PageHeader
        title="Photo Cleanup"
        subtitle="Clean stale gallery images and blacklisted model photos from DB and MinIO."
        actions={
          <>
            <button className="hf-btn" onClick={() => void loadStatus(true)} disabled={loadingStatus}>
              <RefreshCw size={14} className={loadingStatus ? 'spin' : undefined} /> Refresh
            </button>
            <button className="hf-btn" onClick={handleDryRun} disabled={previewing || status?.running}>
              {previewing
                ? <><Loader2 size={14} className="spin" /> Checking...</>
                : <><Search size={14} /> Dry run</>}
            </button>
            <button className="hf-btn danger" onClick={handleStart} disabled={starting || status?.running}>
              {starting
                ? <><Loader2 size={14} className="spin" /> Starting...</>
                : <><Play size={14} /> Start cleanup</>}
            </button>
          </>
        }
      />

      <div className="hf-content">
        {error && (
          <ErrorBanner
            kind="danger"
            message={error}
            onDismiss={() => setError(null)}
            onRetry={() => { setError(null); void loadStatus(true); }}
          />
        )}

        <div className="hf-two-col wide-right">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="hf-card">
              <div className="hf-card-header">
                <div>
                  <h3 className="hf-card-title">Job status</h3>
                  <span className="hf-help" style={{ marginTop: 2 }}>
                    Polls every {POLL_INTERVAL_MS / 1000}s while this page is open.
                  </span>
                </div>
                <span className={`hf-tag ${stateTone(currentState)} sm`}>
                  {status?.running && <Loader2 size={11} className="spin" style={{ marginRight: 4 }} />}
                  {currentState}
                </span>
              </div>
              <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
                  <Kpi label="Models total" value={status?.totalModels ?? 0} />
                  <Kpi label="Objects total" value={totalObjects} />
                  <Kpi label="Deleted" value={status?.objectsDeleted ?? 0} />
                  <Kpi label="Failed" value={status?.objectsFailed ?? 0} />
                  <Kpi label="Progress" value={`${progress}%`} sub={`${fmt(processedObjects)} / ${fmt(totalObjects)}`} />
                </div>

                <div>
                  <div style={{ height: 8, borderRadius: 999, background: 'var(--surface-2)', overflow: 'hidden', border: '1px solid var(--border)' }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${progress}%`,
                        background: status?.objectsFailed ? 'var(--warning)' : 'var(--accent)',
                        transition: 'width 180ms ease'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                  <div className="hf-banner hf-banner-inline">
                    <Clock3 size={14} /> Started: <span className="hf-mono">{formatDateTime(status?.startedAt ?? null)}</span>
                  </div>
                  <div className="hf-banner hf-banner-inline">
                    <CheckCircle2 size={14} /> Finished: <span className="hf-mono">{formatDateTime(status?.finishedAt ?? null)}</span>
                  </div>
                  <div className="hf-banner hf-banner-inline">
                    <RefreshCw size={14} /> Duration: <span className="hf-mono">{formatDuration(status?.startedAt ?? null, status?.finishedAt ?? null)}</span>
                  </div>
                </div>

                {status?.error && (
                  <div className="hf-banner danger">
                    <AlertCircle size={14} /> {status.error}
                  </div>
                )}

                {status?.recentFailures && status.recentFailures.length > 0 && (
                  <div className="hf-card flush">
                    <div className="hf-card-header">
                      <h3 className="hf-card-title">Recent failures</h3>
                      <span className="hf-count">{status.recentFailures.length}</span>
                    </div>
                    <div className="hf-card-body" style={{ padding: 0 }}>
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {status.recentFailures.map((failure, index) => (
                          <li
                            key={`${failure}-${index}`}
                            className="hf-mono"
                            style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--danger)' }}
                          >
                            {failure}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {dryRun && (
              <div className="hf-card">
                <div className="hf-card-header">
                  <div>
                    <h3 className="hf-card-title">Dry-run analytics</h3>
                    <span className="hf-help" style={{ marginTop: 2 }}>
                      Preview only. No DB rows or MinIO objects were changed.
                    </span>
                  </div>
                  <span className="hf-tag warning sm">preview</span>
                </div>
                <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div className="hf-kpi-strip">
                    <Kpi label="Models total" value={dryRun.totalModels} />
                    <Kpi label="Blacklisted models" value={dryRun.blacklistedModels} />
                    <Kpi label="Blacklisted creators" value={dryRun.blacklistedCreatorPageModels} sub="creator_page=true" />
                    <Kpi label="Blacklisted regular" value={dryRun.blacklistedNonCreatorModels} sub="creator_page=false" />
                  </div>

                  <div className="hf-kpi-strip">
                    <Kpi label="Count to delete" value={dryRunObjectsToDelete} sub="unique MinIO objects" />
                    <Kpi label="Photo refs before" value={dryRun.photoRefsBefore} sub="current + gallery rows" />
                    <Kpi label="Photo refs after" value={dryRun.photoRefsAfter} sub="expected DB references" />
                    <Kpi label="Photo refs removed" value={dryRun.photoRefsBefore - dryRun.photoRefsAfter} />
                  </div>

                  <div className="hf-kpi-strip">
                    <Kpi label="Current photos before" value={dryRun.currentPhotoRefsBefore} />
                    <Kpi label="Current photos after" value={dryRun.currentPhotoRefsAfter} />
                    <Kpi label="Current photos to clear" value={dryRun.currentPhotoRefsToClear} />
                    <Kpi label="Inactive gallery rows" value={dryRun.inactiveGalleryRows} />
                  </div>

                  <div className="hf-kpi-strip">
                    <Kpi label="Gallery rows before" value={dryRun.galleryPhotoRowsBefore} />
                    <Kpi label="Gallery rows after" value={dryRun.galleryPhotoRowsAfter} />
                    <Kpi label="Gallery rows to remove" value={dryRun.galleryPhotoRowsToRemove} />
                    <Kpi label="Blacklisted gallery rows" value={dryRun.blacklistedGalleryRows} />
                  </div>

                  <div className="hf-banner warning">
                    <Trash2 size={14} />
                    Cleanup would delete <strong>{fmt(dryRunObjectsToDelete)}</strong> MinIO objects and leave{' '}
                    <strong>{fmt(dryRun.photoRefsAfter)}</strong> DB photo references.
                  </div>
                </div>
              </div>
            )}

            {!dryRun && (
              <div className="hf-kpi-strip">
                <Kpi label="Blacklisted models" value={status?.blacklistedModels ?? 0} />
                <Kpi label="Blacklisted creators" value={status?.blacklistedCreatorPageModels ?? 0} />
                <Kpi label="Photos after" value={status?.photoRefsAfter ?? 0} />
                <Kpi label="Gallery rows removed" value={status?.galleryRowsRemoved ?? 0} />
              </div>
            )}
          </div>

          <aside style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="hf-card">
              <div className="hf-card-header">
                <h3 className="hf-card-title">Cleanup scope</h3>
              </div>
              <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <OptionToggle
                  checked={removeInactiveGalleryPhotos}
                  disabled={status?.running}
                  title="Inactive gallery photos"
                  description="Remove stale gallery rows and their unused MinIO objects."
                  onChange={setRemoveInactiveGalleryPhotos}
                />
                <OptionToggle
                  checked={removeBlacklistedPhotos}
                  disabled={status?.running}
                  title="Blacklisted photos"
                  description="Clear photos for blacklisted models unless creator_page keeps the active photo."
                  onChange={setRemoveBlacklistedPhotos}
                />
              </div>
            </div>

            <div className="hf-card">
              <div className="hf-card-header">
                <h3 className="hf-card-title">Last refresh</h3>
              </div>
              <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div className="hf-mono" style={{ fontSize: 13 }}>
                  {lastRefreshedAt ? lastRefreshedAt.toLocaleTimeString() : '-'}
                </div>
                <div className="hf-help">Status is stored in admin-service memory and resets when the service restarts.</div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
};

export default PhotoCleanupPage;
